import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { NavigationStateService } from '../services/navigation-state.service';
import {
  enableInternalNavigation,
  isBypassPath,
  isEntryPoint,
  isInternalNavigationEnabled,
} from '../utils/navigation-session.util';

/**
 * Navigation access guard to prevent direct URL access
 *
 * This guard ensures users can only access protected routes by navigating
 * through the application flow, not by pasting URLs in a new tab.
 *
 * How it works:
 * 1. When user enters via allowed entry points (/, /profile), a session marker is saved
 * 2. Protected routes only allow access if session marker exists (in-memory or sessionStorage)
 * 3. F5 refresh = allowed (session marker persists in sessionStorage)
 * 4. New tab with direct URL = blocked (no session marker in new tab's sessionStorage)
 * 5. Tab close = session marker cleared (sessionStorage is per-tab)
 *
 * The entry point list and the session marker itself live in
 * utils/navigation-session.util.ts, because the post-login redirect helpers need
 * them too and must stay free of Angular imports.
 *
 * Usage in routes:
 * ```typescript
 * {
 *   path: 'carrier',
 *   canActivate: [navigationAccessGuard, roleGuard],
 *   data: { requiredRoles: [UserRole.CARRIER] },
 *   loadChildren: () => import('./carrier.module').then(m => m.CarrierModule)
 * }
 * ```
 */

export {
  enableInternalNavigation,
  isInternalNavigationEnabled,
  resetNavigationState,
} from '../utils/navigation-session.util';

/**
 * Guard to check if user entered via proper navigation flow
 * Use this on protected routes to prevent direct URL access
 *
 * This blocks:
 * - Direct URL paste in address bar (no session marker in sessionStorage)
 * - Opening protected URL in new tab (no session marker)
 *
 * This ALLOWS:
 * - F5 refresh (session marker persists in sessionStorage)
 * - Normal internal navigation
 */
export const navigationAccessGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);

  // If navigating to an entry point, enable navigation and allow
  if (isEntryPoint(state.url)) {
    enableInternalNavigation();
    return true;
  }

  // If navigating to a bypass path (like /404), allow but don't enable navigation
  if (isBypassPath(state.url)) {
    return true;
  }

  // Check if user has navigated internally (in-memory flag or sessionStorage marker)
  // This handles both normal navigation and page refresh scenarios
  if (isInternalNavigationEnabled()) {
    return true;
  }

  // Direct URL access detected (no session marker) - redirect to 404
  console.warn('Direct URL access blocked:', state.url);
  router.navigate(['/404']);
  return false;
};

/**
 * Guard specifically for entry points (/, /profile)
 * This enables internal navigation when user enters
 */
export const entryPointGuard: CanActivateFn = () => {
  enableInternalNavigation();
  return true;
};

/**
 * Guard for routes that require navigation state (IDs passed via service, not URL)
 * Use this on detail routes where you want to hide IDs from the URL
 *
 * This guard supports both:
 * - Initial navigation (checks Angular router state marker)
 * - Page refresh (checks NavigationStateService which restores from sessionStorage)
 *
 * Usage:
 * ```typescript
 * {
 *   path: 'additional-info',
 *   component: AdditionalDialogComponent,
 *   canActivate: [navigationStateGuard]
 * }
 * ```
 */
export const navigationStateGuard: CanActivateFn = () => {
  const router = inject(Router);
  const navigationStateService = inject(NavigationStateService);

  // Check if this navigation has state (from Angular's router state)
  const navigation = router.getCurrentNavigation();
  const hasRouterState = navigation?.extras?.state?.['__navState'] === true;

  // Check if we have state in the navigation service (restored from sessionStorage on refresh)
  const hasServiceState = navigationStateService.hasState();

  if (hasRouterState || hasServiceState) {
    return true;
  }

  // No state means direct URL access - redirect to 404
  console.warn('Navigation state required but not found - redirecting to 404');
  router.navigate(['/404']);
  return false;
};
